import React, {
  useEffect, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  FDBox,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import { dataAttrs } from 'Helpers/Testing';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { getUtmSource } from 'Views/FreemiumEntry/HasRelationship/Content/Components/utils';
import useHomeContext from 'Context/HomeContext';

const LegacyChiliPiperConciergeComponent = ({
  onSchedulingSuccess,
  onSchedulingEvent,
  relationship,
  shouldAutoTriggerPhone,
  shouldShowMMButton,
}) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const { displayErrorMessage } = useSnackbarContext();
  const { homeResource } = useHomeContext();

  let contactData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    type: '',
    source: getUtmSource(homeResource),
    asset: '',
    journey: '',
  };

  if (relationship) {
    const relationshipUrl = relationship.link('related').url();
    const params = new URL(relationshipUrl).searchParams;
    contactData = {
      firstName: params.get('firstname') || '',
      lastName: params.get('lastname') || '',
      email: params.get('email') || '',
      phone: params.get('phone') || '',
      type: params.get('type') || '',
      source: getUtmSource(homeResource),
      journey: params.get('journey') || '',
      asset: params.get('asset') || '',
    };
  }

  // Load ChiliPiper script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = relationship?.link('related')?.url();
    script.crossOrigin = 'anonymous';
    script.type = 'text/javascript';

    script.onload = () => setIsScriptLoaded(true);
    script.onerror = () => displayErrorMessage('Failed to load scheduler');

    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      document.head.removeChild(script);
    };
  }, [relationship]);

  // Set up message event listeners
  useEffect(() => {
    const handleMessage = (event) => {
      onSchedulingEvent?.(event.data);

      // Handle Amplitude tracking events
      if (window.amplitude) {
        const { action } = event.data;
        if (action === 'booked' || action === 'availability-loaded' || action === 'close') {
          window.amplitude.track(`Scheduler ${action.charAt(0).toUpperCase() + action.slice(1)}`);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSchedulingEvent]);

  // Prepare email to handle spaces
  const prepareLeadEmail = useCallback((email) => {
    return email?.replace(/\s/g, '+') || '';
  }, []);

  function resetChiliPiper() {
    try {
      // This is the line missing from CP's code that causes the bug.
      window.ChiliPiper._marketingApi.popup.isPopupOpen = false;
    } catch (e) {
      // As a fallback, reload if the hack fails
      window.location.reload();
    }
  }

  // Centralized scheduling handler for both phone and video
  const handleScheduling = useCallback((type) => {
    if (!isScriptLoaded || !window.ChiliPiper) {
      return;
    }

    const email = prepareLeadEmail(contactData.email);

    const chiliOptions = {
      trigger: 'ThirdPartyForm',
      lead: {
        ...contactData,
        email,
        queueType: contactData.type,
      },
      event: {
        cp_Journey__c: contactData.journey,
        cp_Type__c: contactData.type,
        cp_Asset__c: contactData.asset,
      },
      onSuccess: () => {
        onSchedulingSuccess?.(type);
        window.location.href = `https://facet.com/thankyou/scheduled/?intro=${type}`;
      },
      onClose: resetChiliPiper,
    };

    const routerName = type === 'phone' ? 'schedule-intro---phone' : 'schedule-intro---zoom';

    const { hostname } = new URL(relationship?.link('related')?.url());
    const subdomain = hostname.split('.')[0];
    window.ChiliPiper.submit(subdomain, routerName, chiliOptions);
  }, [isScriptLoaded, onSchedulingSuccess, contactData, prepareLeadEmail]);

  // Auto-trigger phone scheduling when prop is set and script is loaded
  useEffect(() => {
    if (shouldAutoTriggerPhone && isScriptLoaded) {
      handleScheduling('phone');
    }
  }, [shouldAutoTriggerPhone, isScriptLoaded, handleScheduling]);

  return (
    <FDBox flexGrow={1} width="100%">

      <FDStack direction="column" spacing={2}>
        <FDPrimaryActionButton
          onClick={() => handleScheduling('phone')}
          loading={!isScriptLoaded}
          label="Phone introductory call"
          isFullWidth
          {...dataAttrs('chili-piper-phone-schedule-button')}
        />

        {shouldShowMMButton && (
          <FDSecondaryActionButton
            onClick={() => handleScheduling('video')}
            loading={!isScriptLoaded}
            label="Video introductory call"
            isFullWidth
            {...dataAttrs('chili-piper-video-schedule-button')}
          />
        )}
      </FDStack>
    </FDBox>
  );
};

LegacyChiliPiperConciergeComponent.defaultProps = {
  onSchedulingSuccess: null,
  onSchedulingEvent: null,
  relationship: null,
  shouldAutoTriggerPhone: false,
  shouldShowMMButton: true,
};

LegacyChiliPiperConciergeComponent.propTypes = {
  onSchedulingSuccess: PropTypes.func,
  onSchedulingEvent: PropTypes.func,
  relationship: PropTypes.object,
  shouldAutoTriggerPhone: PropTypes.bool,
  shouldShowMMButton: PropTypes.bool,
};

export default LegacyChiliPiperConciergeComponent;
